/** Normalize DeviceNameToAthleteMapping rows for FleetManager tables. */

export type DeviceMappingRow = Record<string, string>;

const PREFERRED_COLUMN_ORDER = [
  'name',
  'deviceRole',
  'pairName',
  'macFront',
  'macRear',
  'athleteId',
  'athleteNumber',
  'eventId',
  'raceId',
  'draftingThresholdSeconds',
  'description',
  'desc',
  'simId',
  'sim_id',
  'sim',
  'iccid',
] as const;

export function resolveDeviceMappingId(row: DeviceMappingRow | Record<string, unknown> | null | undefined): string {
  if (!row || typeof row !== 'object') return '';
  const record = row as Record<string, unknown>;
  const fromName = String(record.name ?? '').trim();
  if (fromName) return fromName;
  const fromDeviceId = String(record.deviceId ?? record.device_id ?? '').trim();
  return fromDeviceId;
}

function inferDeviceRole(name: string): string {
  if (/^rear_/i.test(name)) return 'rear';
  if (/^front_/i.test(name)) return 'front';
  return '';
}

function inferPairName(name: string): string {
  if (/^rear_/i.test(name)) return `Front_${name.slice(5)}`;
  if (/^front_/i.test(name)) return `Rear_${name.slice(6)}`;
  return '';
}

/** Collapse legacy deviceId into name and fill role/pair hints when missing. */
export function normalizeDeviceMappingRow(row: DeviceMappingRow): DeviceMappingRow {
  const id = resolveDeviceMappingId(row);
  const out: DeviceMappingRow = {};

  for (const [key, value] of Object.entries(row)) {
    if (key === 'deviceId' || key === 'device_id') continue;
    out[key] = value == null ? '' : String(value);
  }

  if (id) out.name = id;

  if (!out.deviceRole?.trim()) {
    const role = inferDeviceRole(id);
    if (role) out.deviceRole = role;
  }

  if (!out.pairName?.trim()) {
    const pair = inferPairName(id);
    if (pair) out.pairName = pair;
  }

  return out;
}

export function buildDeviceMappingColumns(rows: DeviceMappingRow[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    Object.keys(row).forEach((key) => {
      if (key !== 'deviceId' && key !== 'device_id') keys.add(key);
    });
  }

  const ordered = PREFERRED_COLUMN_ORDER.filter((key) => keys.has(key));
  const rest = Array.from(keys)
    .filter((key) => !ordered.includes(key as (typeof PREFERRED_COLUMN_ORDER)[number]))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  if (!ordered.includes('name') && keys.has('name')) {
    return ['name', ...ordered.filter((key) => key !== 'name'), ...rest.filter((key) => key !== 'name')];
  }

  return [...ordered, ...rest];
}

export function normalizeDeviceMappingRows(rawRows: DeviceMappingRow[]): {
  rows: DeviceMappingRow[];
  columns: string[];
} {
  const normalized = rawRows
    .map((row) => normalizeDeviceMappingRow(row))
    .filter((row) => resolveDeviceMappingId(row).length > 0);

  // Synthesize Front_<n> rows from Rear_<n> rows when absent.
  // The mapping table only stores rear rows; front devices share the same
  // athlete/race assignment as their paired rear.
  const existingIds = new Set(normalized.map((r) => resolveDeviceMappingId(r).toLowerCase()));
  const synthetic: DeviceMappingRow[] = [];
  for (const row of normalized) {
    const id = resolveDeviceMappingId(row);
    if (!/^rear_/i.test(id)) continue;
    const frontId = `Front_${id.slice(5)}`;
    if (existingIds.has(frontId.toLowerCase())) continue;
    synthetic.push({ ...row, name: frontId, deviceRole: 'front', pairName: id });
  }

  const rows = [...normalized, ...synthetic].sort((a, b) =>
    resolveDeviceMappingId(a).localeCompare(resolveDeviceMappingId(b), undefined, { sensitivity: 'base' }),
  );

  const columns = buildDeviceMappingColumns(rows);
  const projected = rows.map((row) => {
    const out: DeviceMappingRow = {};
    for (const col of columns) {
      out[col] = row[col] ?? '';
    }
    return out;
  });

  return { rows: projected, columns };
}

export function deviceMappingColumnLabel(column: string): string {
  const normalized = String(column || '').trim().toLowerCase();
  if (normalized === 'name') return 'Device ID';
  if (normalized === 'devicerole') return 'Role';
  if (normalized === 'pairname') return 'Pair';
  if (normalized === 'macfront') return 'Front MAC';
  if (normalized === 'macrear') return 'Rear MAC';
  return column;
}

export function buildDeviceMappingIdSet(rows: DeviceMappingRow[]): Set<string> {
  const set = new Set<string>();
  for (const row of rows) {
    const id = resolveDeviceMappingId(row);
    if (id) set.add(id);
  }
  return set;
}

export function blankDeviceMappingRow(columns: string[]): DeviceMappingRow {
  const out: DeviceMappingRow = {};
  for (const col of columns) out[col] = '';
  return out;
}
